import { useId } from 'react'
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { usd, usdAxis } from '@/lib/money'
import { formatDay } from '@/lib/time'
import { cn } from '@/lib/utils'
import { Legend } from './Legend'
import { useChartColors } from './useChartColors'

export interface TrendDatum {
  date: string
  revenue: number
  cost: number
  profit: number
}

export interface TrendChartProps {
  data: TrendDatum[]
  /** Spend comes in (spendShown): with false only revenue is drawn — a flat
   *  $0 spend line would read as "spent nothing". */
  showSpend?: boolean
  height?: number
  /** A fixed width (tests; jsdom has no layout). Otherwise it fills its box. */
  width?: number
  /** Fewer axis labels, for a phone. */
  compact?: boolean
  className?: string
}

/** Revenue and spend as areas and profit as a thin line, by day (plan 1-3:
 *  수익 chart-1, 지출 chart-2, 순이익 the text colour). The legend sits
 *  above; the tooltip writes money with `usd`. The one recharts user. */
export function TrendChart({ data, showSpend = true, height = 260, width, compact, className }: TrendChartProps) {
  const c = useChartColors()
  const id = useId().replace(/:/g, '')
  const legend = [
    { label: '수익', swatch: 'bg-chart-1' },
    ...(showSpend ? [{ label: '지출', swatch: 'bg-chart-2' }, { label: '순이익', swatch: 'bg-foreground' }] : []),
  ]
  const chart = (
    <ComposedChart data={data} width={width} height={width ? height : undefined} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
      <defs>
        <linearGradient id={`${id}-rev`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c.revenue} stopOpacity={0.24} />
          <stop offset="100%" stopColor={c.revenue} stopOpacity={0.02} />
        </linearGradient>
        <linearGradient id={`${id}-spend`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c.spend} stopOpacity={0.2} />
          <stop offset="100%" stopColor={c.spend} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <CartesianGrid vertical={false} stroke={c.grid} />
      <XAxis
        dataKey="date"
        tickLine={false}
        axisLine={{ stroke: c.grid }}
        tick={{ fill: c.axis, fontSize: 11 }}
        tickFormatter={(v: string) => formatDay(v, undefined, false)}
        interval={compact ? Math.max(0, Math.ceil(data.length / 4) - 1) : 'preserveStartEnd'}
        minTickGap={compact ? 24 : 12}
      />
      <YAxis tickLine={false} axisLine={false} width={compact ? 44 : 52} tick={{ fill: c.axis, fontSize: 11 }} tickFormatter={(v: number) => usdAxis(v)} />
      <Tooltip
        cursor={{ stroke: c.grid }}
        content={({ active, payload, label }) => {
          if (!active || !payload?.length) return null
          const p = payload[0].payload as TrendDatum
          return (
            <div className="rounded-md border border-border bg-card px-3 py-2 text-meta shadow-overlay">
              <div className="mb-1 font-medium text-foreground">{formatDay(String(label ?? p.date))}</div>
              <div className="grid grid-cols-[auto_auto] gap-x-3 tabular-nums">
                <span className="text-muted-foreground">수익</span>
                <span className="text-right text-foreground">{usd(p.revenue)}</span>
                {showSpend && (
                  <>
                    <span className="text-muted-foreground">지출</span>
                    <span className="text-right text-foreground">{usd(p.cost)}</span>
                    <span className="text-muted-foreground">순이익</span>
                    <span className="text-right text-foreground">{usd(p.profit)}</span>
                  </>
                )}
              </div>
            </div>
          )
        }}
      />
      <Area type="monotone" dataKey="revenue" name="수익" stroke={c.revenue} strokeWidth={1.8} fill={`url(#${id}-rev)`} isAnimationActive={false} />
      {showSpend && <Area type="monotone" dataKey="cost" name="지출" stroke={c.spend} strokeWidth={1.6} fill={`url(#${id}-spend)`} isAnimationActive={false} />}
      {showSpend && <Line type="monotone" dataKey="profit" name="순이익" stroke={c.profit} strokeWidth={1.2} dot={false} isAnimationActive={false} />}
    </ComposedChart>
  )
  return (
    <figure className={cn('min-w-0 space-y-2', className)}>
      <Legend items={legend} />
      <div style={{ height }} aria-label={`일별 ${showSpend ? '수익·지출·순이익' : '수익'} 추이, ${data.length}일`} role="img">
        {width ? chart : <ResponsiveContainer width="100%" height="100%">{chart}</ResponsiveContainer>}
      </div>
    </figure>
  )
}
